import { statusStyle } from '../helpers/constant';
import {
    getStatus,
    getCryptoStatus,
    getAvailableCurrencies,
    fiatCurrencyList,
    getCurrencySymbol,
    toFixedTrunc,
} from '../../client/Juno/helpers';
import { cryptoCurrencies } from '../helpers/constant';
import { useSelector } from 'react-redux';

// Helper functions to reduce repetition
const createStatusField = (statusText, statusKey) => ({
    label: 'Status',
    value: (
        <span className={`px-2 py-1 text-xs font-semibold ${statusStyle[statusKey]}`}>
            {statusText}
        </span>
    ),
});

const createTransactionIdField = transactionId => ({
    label: 'Transaction Id',
    value: transactionId || '-',
    copyText: transactionId,
});

const createCurrencyField = (currency, label = 'Currency') => {
    const { balancesCurrencies } = useSelector(state => state.juno);
    const imageBaseUrl = useSelector(state => state.config.image_base_url);

    if (!currency) return null;

    const matchedCurrency = fiatCurrencyList?.find(item => item.currencyShortName === currency);
    const displayCurrency = matchedCurrency?.currencyFullName || currency;

    return {
        label,
        value: (
            <span className="flex gap-2">
                <img
                    src={`${imageBaseUrl}${
                        fiatCurrencyList?.find(item => item.text === currency)?.img
                    }`}
                    alt="currency"
                    className="w-5 h-5"
                />
                <span className='uppercase'>{displayCurrency}</span>
            </span>
        ),
        copyText: currency,
    };
};

const createCryptoField = (cryptoId, data, label = 'Currency') => {
    if (!cryptoId) return null;

    return {
        label,
        value: (
            <span className="flex gap-2">
                <img
                    src={`https://dev.junomoney.org/images${
                        getAvailableCurrencies([{ ...data }])[0]?.img
                    }`}
                    alt="currency"
                    className="w-5 h-5"
                />
                <span>{cryptoId}</span>
            </span>
        ),
        copyText: cryptoId,
    };
};

const createAmountField = (currency, amount, label = 'Amount') => ({
    label,
    value: `${getCurrencySymbol(currency)} ${toFixedTrunc(amount, 2)}`,
    copyText: `${getCurrencySymbol(currency)} ${toFixedTrunc(amount, 2)}`,
});

const createCryptoAmountField = (cryptoId, amount, label = 'Amount (Crypto)', decimals = 6) => ({
    label,
    value: `${getCurrencySymbol(cryptoId)} ${toFixedTrunc(amount, decimals)}`,
    copyText: `${getCurrencySymbol(cryptoId)} ${toFixedTrunc(amount, decimals)}`,
});

const createTransactionFeeField = (currency, fee) => ({
    label: 'Transaction Fee:',
    value: `${getCurrencySymbol(currency)} ${toFixedTrunc(fee, 2) || 0}`,
    copyText: `${getCurrencySymbol(currency)} ${toFixedTrunc(fee, 2) || 0}`,
});

const createBalanceField = (currency, balance, decimals = 2) => ({
    label: 'Balance after transaction:',
    value: `${getCurrencySymbol(currency)} ${toFixedTrunc(balance, decimals)}`,
    copyText: `${getCurrencySymbol(currency)} ${toFixedTrunc(balance, decimals)}`,
});

const createField = (label, value, copyText = value) => {
    if (value === undefined || value === null) return null;

    return {
        label,
        value,
        copyText,
    };
};

export const formatTransactionDetails = data => {
    const { type } = data || {};
    const details = data?.transactionDetails?.[0] || {};
    const transactionFee = data?.transactionFee;
    const balanceBeforeDeduction = transactionFee
        ? Number(details?.amount) + Number(transactionFee)
        : details?.amount;

    const balanceAfterDeduction = transactionFee
        ? Number(details?.amount) - Number(transactionFee)
        : details?.amount;

    const statusText =
        data?.currencyType === 'fiat'
            ? getStatus(data?.status?.code, data?.type)
            : getCryptoStatus(data?.status?.code, data?.status?.message);

    const statusKey = statusText.toUpperCase().replace('/', '');

    const baseFields = [createStatusField(statusText, statusKey)];

    // Add transaction ID if available to all transaction types
    if (data?.transactionId) {
        baseFields.push(createTransactionIdField(data.transactionId));
    }

    // Specific fields based on transaction type
    switch (type) {
        case 'Deposit': {
            const fields = [
                ...baseFields,
                details?.fromAddress && createField('From', details.fromAddress),
                details?.toAddress && createField('To', details.toAddress),
                details?.Txid && createField('Txid', details.Txid),
                details?.USDAmount &&
                    createField('Amount (USD)', `$ ${toFixedTrunc(details.USDAmount, 2)}`),
                details?.remitterName && createField('Remitter name', details.remitterName),
                details?.amount &&
                    createField(
                        !transactionFee ? 'Amount' : 'Amount before deduction',
                        `${getCurrencySymbol(details.currency)} ${toFixedTrunc(details?.amount, 2)}`,
                    ),
                details?.amount &&
                    createField(
                        'Amount after deduction:',
                        `${getCurrencySymbol(details.currency)} ${toFixedTrunc(balanceAfterDeduction, 2)}`,
                    ),
                createCurrencyField(details?.currency),
                data?.balanceAfterTransaction &&
                    createBalanceField(details.currency, data.balanceAfterTransaction),
                createTransactionFeeField(data.currency, transactionFee),
                details?.reference && createField('Reference', details.reference),
            ];
            return fields.filter(Boolean);
        }

        case 'Withdrawal': {
            const fields = [
                ...baseFields,
                createCurrencyField(details?.currency),
                createField(
                    !transactionFee ? 'Amount' : 'Amount before deduction',
                    `${getCurrencySymbol(data.currency)} ${toFixedTrunc(balanceBeforeDeduction, 2)}`,
                ),
                createTransactionFeeField(data.currency, transactionFee),
                createBalanceField(data.currency, data.balanceAfterTransaction),
                transactionFee &&
                    createField(
                        'Amount after deduction:',
                        `${getCurrencySymbol(data.currency)} ${toFixedTrunc(details.amount, 2) || '-'}`,
                    ),
                createField('Reference', details?.reference || '-'),
                createField('Beneficiary name', details?.beneficiaryName || '-'),
                createField('Beneficiary address', details?.beneficiaryAddress || '-'),
                createField('Beneficiary country', details?.beneficiaryCountry || '-'),
                createField('Bank name', details?.bankName || '-'),
                createField('Bank address', details?.bankAddress || '-'),
                createField('Bank country', details?.bankCountry || '-'),
                createField('IBAN', details?.iban || '-'),
                createField('SWIFT', details?.swift || '-'),
                createField('Sort code', details?.sortCode || '-'),
                createField('Beneficiary account number', details?.beneficiaryAccountNumber || '-'),
            ];
            return fields.filter(Boolean);
        }

        case 'Payment Out': {
            const fields = [
                ...baseFields,
                createCurrencyField(details?.currency),
                createField('Beneficiary name', details?.beneficiaryName || '-'),
                createField('Beneficiary address', details?.beneficiaryAddress || '-'),
                createField('Bank name', details?.bankName || '-'),
                createField('Bank address', details?.bankAddress || '-'),
                createField('Bank country', details?.bankCountry || '-'),
                createField('IBAN', details?.iban || '-'),
                createField('SWIFT', details?.swift || '-'),
                createField('Sort code', details?.sortCode || '-'),
                createField('Beneficiary account number', details?.beneficiaryAccountNumber || '-'),
                createField('Beneficiary country', details?.beneficiaryCountry || '-'),
                createField(
                    !transactionFee ? 'Amount' : 'Amount before deduction',
                    `${getCurrencySymbol(data.currency)} ${toFixedTrunc(balanceBeforeDeduction, 2)}`,
                ),
                transactionFee &&
                    createField(
                        'Amount after deduction:',
                        `${getCurrencySymbol(data.currency)} ${toFixedTrunc(details.amount, 2) || '-'}`,
                    ),
                createField('Reference', details?.reference || '-'),
                createBalanceField(data.currency, data.balanceAfterTransaction),
                createTransactionFeeField(data.currency, transactionFee),
            ];
            return fields.filter(Boolean);
        }

        case 'FX': {
            const fields = [
                ...baseFields,
                createCurrencyField(details?.currency),
                createCurrencyField(details?.fromCurrency, 'Currency exchanged from'),
                createCurrencyField(details?.toCurrency, 'Currency exchanged to'),
                createField('FX rates', toFixedTrunc(details?.fxrate, 6)),
                createField(
                    'Amount (exchanged from currency)',
                    `${getCurrencySymbol(details?.fromCurrency)} ${toFixedTrunc(details?.fromAmount, 2)}`,
                ),
                createField(
                    'Amount (exchanged to currency)',
                    `${getCurrencySymbol(details?.toCurrency)} ${toFixedTrunc(details?.toAmount, 2)}`,
                ),
                createBalanceField(details?.fromCurrency, data?.balanceAfterTransaction),
            ];
            return fields.filter(Boolean);
        }

        case 'Transfer': {
            // Special status handling for Transfer case
            const transferStatus = getStatus(data?.status?.code);
            const transferStatusKey = transferStatus.toUpperCase().replace('/', '');

            const fields = [
                {
                    label: 'Status',
                    value: (
                        <span
                            className={`px-2 py-1 text-xs font-semibold ${statusStyle[transferStatusKey]}`}>
                            {transferStatus}
                        </span>
                    ),
                },
                data?.transactionId && createTransactionIdField(data.transactionId),
                createCurrencyField(details?.currency),
                details?.email && createField('Juno Money Email', details.email),
                createField(
                    !transactionFee ? 'Amount' : 'Amount before deduction',
                    `${getCurrencySymbol(data.currency)} ${toFixedTrunc(balanceBeforeDeduction, 2)}`,
                ),
                transactionFee &&
                    createField(
                        'Amount after deduction',
                        `${getCurrencySymbol(data.currency)} ${toFixedTrunc(details.amount, 2)}`,
                    ),
                data.transferInOut === 'Out' &&
                    createTransactionFeeField(data.currency, transactionFee),
                data.transferInFee &&
                    createField(
                        'Merchant Fee',
                        `${getCurrencySymbol(data.currency)} ${toFixedTrunc(data.transferInFee, 2)}`,
                    ),
                details?.accountName && createField('Juno Money Account Name', details.accountName),
                details?.reference && createField('Reference', details.reference),
                data?.balanceAfterTransaction &&
                    createBalanceField(data.currency, data.balanceAfterTransaction),
            ];
            return fields.filter(Boolean);
        }

        case 'Buy':
        case 'Sell': {
            if (localStorage.getItem('activeCurrencyPage') === 'fiat') {
                const label = type === 'Buy' ? 'To' : 'From';
                const fields = [
                    ...baseFields,
                    createCurrencyField(details?.fiatCurrency),
                    createField(
                        'Amount',
                        `${getCurrencySymbol(details?.fiatCurrency)} ${toFixedTrunc(details.fiatAmount, 2)}` ||
                            '-',
                    ),
                    createField(label, 'Juno Pay Ltd' || '-'),
                ];
                return fields.filter(Boolean);
            }
            const fields = [
                ...baseFields,
                createCryptoField(details?.cryptoId, data),
                createField('CryptoAmount', toFixedTrunc(details?.cryptoAmount, 6) || '-'),
                createCurrencyField(details?.fiatCurrency, 'FiatCurrency'),
                createField('FiatAmount', toFixedTrunc(details?.fiatAmount, 2) || '-'),
                createField('Transaction Fee', `${toFixedTrunc(data?.transactionFee, 2) || '-'}`),
                createField(
                    'Total Amount',
                    `${getCurrencySymbol(details?.cryptoId)} ${toFixedTrunc(details?.totalAmount, 6) || '-'}`,
                ),
                data?.balanceAfterTransaction &&
                    createBalanceField(details?.cryptoId, data?.balanceAfterTransaction, 6),
            ];
            return fields.filter(Boolean);
        }

        case 'Send': {
            const fields = [
                ...baseFields,
                details?.fromAddress && createField('From Address', details?.fromAddress),
                details?.toAddress && createField('To Address', details?.toAddress),
                createCryptoField(details?.cryptoId, data),
                details?.cryptoAmount &&
                    createField('Amount (Crypto)', `${details?.cryptoId} ${details?.cryptoAmount}`),
                transactionFee && createTransactionFeeField(data?.currency, transactionFee),
                createBalanceField(data?.currency, data?.balanceAfterTransaction),
            ];
            return fields.filter(Boolean);
        }

        case 'Receive': {
            // Add function for sliced address if missing
            const getSlicedAddress = address => address || '-'; // Implement proper slicing if needed

            const fields = [
                ...baseFields,
                details?.fromWalletAddress &&
                    createField(
                        'From Wallet Address',
                        getSlicedAddress(details?.fromWalletAddress),
                    ),
                details?.toWalletAddress &&
                    createField('To Wallet Address', getSlicedAddress(details?.toWalletAddress)),
                createCryptoField(details?.cryptoId, data),
                transactionFee && createField('Transaction Fee', transactionFee),
                details?.totalAmount && createField('Total Amount', details?.totalAmount),
                details?.cryptoAmount &&
                    createCryptoAmountField(
                        details.cryptoId,
                        details?.cryptoAmount,
                        'Crypto Amount',
                    ),
                data?.balanceAfterTransaction &&
                    createBalanceField(details.cryptoId, data?.balanceAfterTransaction, 6),
            ];
            return fields.filter(Boolean);
        }

        case 'Exchange': {
            const fields = [
                ...baseFields,
                {
                    label: 'Currency',
                    value: (
                        <span className="flex gap-2">
                            <img
                                src={`https://dev.junomoney.org/images${
                                    cryptoCurrencies.find(
                                        item => item.text === details.fromCryptoId,
                                    )?.img
                                }`}
                                alt="currency"
                                className="w-5 h-5"
                            />
                            <span>{details?.fromCryptoId}</span>
                        </span>
                    ),
                    copyText: details?.fromCryptoId,
                },
                details?.fromCryptoAmount &&
                    createField('From Crypto Amount', toFixedTrunc(details?.fromCryptoAmount, 6)),
                {
                    label: 'Currency',
                    value: (
                        <span className="flex gap-2">
                            <img
                                src={`https://dev.junomoney.org/images${
                                    cryptoCurrencies.find(item => item.text === details.toCryptoId)
                                        ?.img
                                }`}
                                alt="currency"
                                className="w-5 h-5"
                            />
                            <span>{details?.toCryptoId}</span>
                        </span>
                    ),
                    copyText: details?.toCryptoId,
                },
                details?.toCryptoAmount &&
                    createField('To Crypto Amount', toFixedTrunc(details?.toCryptoAmount, 6)),
                transactionFee && createField('Transaction Fee', transactionFee),
                data?.balanceAfterTransaction &&
                    createField(
                        'Balance after transaction',
                        `${toFixedTrunc(data?.balanceAfterTransaction, 6)}`,
                    ),
            ];
            return fields.filter(Boolean);
        }

        default:
            return [
                {
                    label: 'Info',
                    value: 'Unknown transaction type',
                    copyText: '',
                },
            ];
    }
};
